import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LevelDTO } from '@/features/academic-structure/application/dto/LevelDTO';

/**
 * Levels Store State Interface
 * Manages global state for academic levels
 */
interface LevelsState {
  // Data
  levels: LevelDTO[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setLevels: (levels: LevelDTO[]) => void;
  addLevel: (level: LevelDTO) => void;
  updateLevel: (id: number, level: Partial<LevelDTO>) => void;
  deleteLevel: (id: number) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getLevelById: (id: number) => LevelDTO | undefined;
  clearLevels: () => void;
  reset: () => void;
}

/**
 * Initial state for levels store
 */
const initialState = {
  levels: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Levels Store
 *
 * Global state management for levels using Zustand.
 * Features:
 * - Persistent storage (localStorage)
 * - CRUD operations
 * - Loading and error states
 * - Helper selectors
 *
 * Usage:
 * ```typescript
 * const { levels, isLoading, setLevels } = useLevelsStore();
 * ```
 *
 * Note: This store should be populated by use cases, not repositories directly.
 * Follow hexagonal architecture principles.
 */
export const useLevelsStore = create<LevelsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setLevels: (levels) =>
        set({
          levels,
          lastFetched: Date.now(),
          error: null,
        }),

      addLevel: (level) =>
        set((state) => ({
          levels: [...state.levels, level],
          lastFetched: Date.now(),
        })),

      updateLevel: (id, updatedLevel) =>
        set((state) => ({
          levels: state.levels.map((level) =>
            level.id === id ? { ...level, ...updatedLevel } : level
          ),
          lastFetched: Date.now(),
        })),

      deleteLevel: (id) =>
        set((state) => ({
          levels: state.levels.filter((level) => level.id !== id),
          lastFetched: Date.now(),
        })),

      // State Management
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      // Helpers
      getLevelById: (id) => {
        const state = get();
        return state.levels.find((level) => level.id === id);
      },

      clearLevels: () =>
        set({
          levels: [],
          lastFetched: null,
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'levels-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not loading/error states
      partialize: (state) => ({
        levels: state.levels,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized component rendering
 * Use these to select specific parts of the state
 */
export const levelsSelectors = {
  // Select all levels
  selectLevels: (state: LevelsState) => state.levels,

  // Select loading state
  selectIsLoading: (state: LevelsState) => state.isLoading,

  // Select error state
  selectError: (state: LevelsState) => state.error,

  // Select level by ID (returns a selector function)
  selectLevelById: (id: number) => (state: LevelsState) =>
    state.levels.find((level) => level.id === id),

  // Select levels count
  selectLevelsCount: (state: LevelsState) => state.levels.length,

  // Select sorted levels (by name)
  selectSortedLevels: (state: LevelsState) =>
    [...state.levels].sort((a, b) => a.name.localeCompare(b.name)),

  // Check if data is stale (older than 5 minutes)
  selectIsStale: (state: LevelsState) => {
    if (!state.lastFetched) return true;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - state.lastFetched > fiveMinutes;
  },
};
